import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { SpeechBubble } from '../components/ui/SpeechBubble';
import { FloatingDecor } from '../components/ui/FloatingDecor';
const FALLBACK_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='600'%3E%3Crect width='100%25' height='100%25' fill='%23FFE4ED'/%3E%3C/svg%3E";
const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = FALLBACK_IMG;
};
const sectionEntrance = {
  initial: {
    opacity: 0,
    y: 40,
    scale: 0.96
  },
  whileInView: {
    opacity: 1,
    y: 0,
    scale: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    type: 'spring',
    stiffness: 200,
    damping: 20
  }
};
const popWord = {
  initial: {
    opacity: 0,
    scale: 0.8,
    rotate: 0
  },
  animate: {
    opacity: 1,
    scale: 1,
    rotate: -2
  },
  transition: {
    type: 'spring',
    stiffness: 300,
    damping: 15,
    delay: 0.3
  }
};
// 4 expertises de l'agence, chacune sous le nom JANY (contenu Influence validé ; Site Web / Consulting / Studio = brouillon à valider avec Marine)
export function Services() {
  const services = [{
    title: 'JANY AGENCY',
    subtitle: 'Influence & Talents',
    desc: "Nous représentons des créateurs de contenu et orchestrons des campagnes sponsorisées sur Instagram et TikTok : casting, stratégie créative, négociation des contrats et suivi de performance (reach, engagement, ROI).",
    badge: 'rose-sparkle' as const,
    image: 'https://images.unsplash.com/photo-1543525469-65b61cc2bc06?auto=format&fit=crop&q=80&w=800',
    bgColor: 'bg-jany-pink',
    textColor: 'text-white',
    descColor: 'text-white/90',
    decorColor: 'white' as const,
    showLegal: true
  }, {
    title: 'JANY WEBSITE',
    subtitle: 'Création de Site Web',
    desc: "Sites vitrine et e-commerce sur-mesure, pensés pour convertir : identité visuelle cohérente avec votre marque, expérience utilisateur soignée, intégration responsive et accompagnement dans la durée pour faire évoluer votre site.",
    badge: 'yellow-star' as const,
    image: 'https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?auto=format&fit=crop&q=80&w=800',
    bgColor: 'bg-jany-yellow',
    textColor: 'text-jany-dark',
    descColor: 'text-jany-dark/80',
    decorColor: 'dark' as const,
    showLegal: false
  }, {
    title: 'JANY CONSULTING',
    subtitle: 'Jany Academy — Formations',
    desc: "Des formations concrètes pour monter en compétences : créateurs de contenu accompagnés pour structurer et monnétiser leur audience, marques formées aux fondamentaux d'une stratégie d'influence efficace, ateliers et coaching personnalisés.",
    badge: 'lightrose-eye' as const,
    image: 'https://images.unsplash.com/photo-1503428593586-e225b39bddfe?auto=format&fit=crop&q=80&w=800',
    bgColor: 'bg-jany-pink-pale',
    textColor: 'text-jany-dark',
    descColor: 'text-jany-dark/80',
    decorColor: 'pink' as const,
    showLegal: false
  }, {
    title: 'JANY STUDIO',
    subtitle: 'Production Vidéo & Spots Publicitaires',
    desc: "De la captation au montage : contenus courts optimisés pour les réseaux sociaux (reels, UGC, spots publicitaires), direction artistique fidèle à votre identité de marque, pour des vidéos qui captent l'attention et convertissent.",
    badge: 'blue-palmtree' as const,
    image: 'https://images.unsplash.com/photo-1764162051349-7cc989a1399d?auto=format&fit=crop&q=80&w=800',
    bgColor: 'bg-jany-blue',
    textColor: 'text-jany-dark',
    descColor: 'text-jany-dark/80',
    decorColor: 'dark' as const,
    showLegal: false
  }];
  return <div className="pt-32 pb-0 overflow-hidden" data-id="element-317">
      <div className="container mx-auto px-6 md:px-12 mb-20 relative" data-id="element-318">
        <FloatingDecor color="pink" count={4} data-id="element-319" />
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-320">
          <SpeechBubble text="OUR EXPERTISE" color="orange" className="mb-8" data-id="element-321" />
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-6xl md:text-8xl mb-8 text-jany-dark" data-id="element-322">
            Nos{' '}
            <motion.span variants={popWord} initial="initial" animate="animate" className="inline-block text-jany-orange italic" data-id="element-323">
              Expertises
            </motion.span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-2xl text-jany-dark/80 font-medium" data-id="element-324">
            Une agence pluridisciplinaire — JANY AGENCY, JANY WEBSITE, JANY
            CONSULTING et JANY STUDIO — pour des projets cohérents de bout en
            bout.
          </motion.p>
        </div>
      </div>

      <div className="flex flex-col" data-id="element-325">
        {services.map((service, idx) => <motion.section key={idx} {...sectionEntrance} className={`${service.bgColor} py-24 ${idx > 0 ? '-mt-10 rounded-t-[4rem] relative z-' + idx * 10 : 'rounded-t-[4rem] relative z-10'}`} data-id="element-326">
            <FloatingDecor color={service.decorColor} count={6} data-id="element-327" />
            <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-328">
              <div className={`flex flex-col ${idx % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-16`} data-id="element-329">
                <div className="flex-1 w-full" data-id="element-330">
                  <motion.div whileHover={{
                scale: 1.02,
                rotate: idx % 2 === 0 ? 1 : -1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 15
              }} className="relative aspect-square md:aspect-[4/3] rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white" data-id="element-331">
                    <img src={service.image} alt={service.title} onError={handleImgError} className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" data-id="element-332" />
                  </motion.div>
                </div>

                <div className={`flex-1 space-y-6 ${service.textColor}`} data-id="element-333">
                  <Badge variant={service.badge} size="xl" label={`0${idx + 1}`} data-id="element-334" />
                  <div data-id="element-335">
                    <p className={`text-sm font-bold uppercase tracking-[0.2em] mb-2 ${service.descColor}`} data-id="element-336">
                      {service.subtitle}
                    </p>
                    <h2 className="text-5xl md:text-6xl font-display" data-id="element-337">
                      {service.title}
                    </h2>
                  </div>
                  <p className={`text-xl leading-relaxed font-medium ${service.descColor}`} data-id="element-338">
                    {service.desc}
                  </p>
                  {service.showLegal && <motion.div whileHover={{
                scale: 1.02
              }} className="bg-white p-6 rounded-3xl shadow-md flex items-start gap-4 mt-8 text-jany-dark" data-id="element-339">
                      <ShieldCheck className="text-jany-orange shrink-0 mt-1" size={28} data-id="element-340" />
                      <p className="text-base font-medium" data-id="element-341">
                        <strong data-id="element-342">Engagement Légal :</strong> Jany & Co s'engage à
                        respecter scrupuleusement la loi du 9 juin 2023 sur
                        l'influence commerciale (#Partenariat, #Publicité), pour
                        protéger les marques et les talents.
                      </p>
                    </motion.div>}
                </div>
              </div>
            </div>
          </motion.section>)}

        <motion.section {...sectionEntrance} className="bg-jany-orange py-32 -mt-10 rounded-t-[4rem] relative z-50 text-center" data-id="element-343">
          <FloatingDecor color="white" count={8} data-id="element-344" />
          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-345">
            <motion.div whileHover={{
            scale: 1.02
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} className="bg-white rounded-[4rem] p-16 shadow-xl max-w-4xl mx-auto" data-id="element-346">
              <SpeechBubble text="CUSTOM PROJECTS" color="blue" className="mb-6" data-id="element-347" />
              <h3 className="text-4xl md:text-5xl mb-6 font-display text-jany-dark" data-id="element-348">
                Un besoin spécifique ?
              </h3>
              <p className="text-xl text-jany-dark/80 mb-10 font-medium" data-id="element-349">
                Nous créons également des dispositifs sur-mesure combinant
                plusieurs de nos expertises (campagne d'influence + site web +
                contenu vidéo, par exemple).
              </p>
              <Button href="/contact" variant="pink" size="lg" className="text-xl px-10 py-5" data-id="element-350">
                Parlons de votre projet
              </Button>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </div>;
}